import { isNil, isNull, pickBy } from 'lodash';
import { isReferencedInComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';

export function isComponentExcludedFromCollectionView(component: API.AnyComponent): boolean {
  return isReferencedInComponent(component);
}

export function isComponentHidden(
  viewTemplate?: API.ViewTemplateFragment,
  viewOptions?: NonNullable<API.ViewTemplateFragment['componentsViewOptions']>[number],
  component?: API.AnyComponent,
) {
  const overrideValue = viewOptions?.overrides?.isHidden ?? viewOptions?.isHidden;

  if (!isNil(overrideValue)) {
    return overrideValue;
  }

  return component?.isHidden === true || viewTemplate?.defaultHiddenComponents === true;
}

export function isComponentReadOnlyInView(component, viewTemplate: API.ViewTemplateFragment) {
  const viewOptions = getComponentViewOptions(component, viewTemplate);
  return isComponentReadOnly(
    component,
    viewOptions?.overrides,
    viewTemplate.defaultReadonlyComponents ?? undefined,
  );
}

export function getComponentViewOptions(component, viewTemplate: API.ViewTemplateFragment) {
  return viewTemplate?.componentsViewOptions?.find(option => component.id === option.componentId);
}

export function isComponentReadOnly(
  component,
  overrides,
  templateDefaultReadonly?: boolean,
): boolean {
  const overrideReadonly = overrides?.readonly;
  if (!isNil(overrideReadonly)) {
    return overrideReadonly;
  }
  return (
    (!isNil(component.readonly) && component.readonly) ||
    (!isNil(templateDefaultReadonly) && templateDefaultReadonly)
  );
}

// because all props that are *not* overridden are set to null in the view template,
// we have to specifically pick them out to avoid clearing values.
// limiting to a subset of keys used in the UI.
// readonly and isHidden are handled above.
const handledOverrides = ['label', 'placeholder'];

export function getComponentWithOverrides(
  component: API.AnyComponent,
  viewTemplate: API.ViewTemplateFragment,
): API.AnyComponent {
  const viewOptions = viewTemplate.componentsViewOptions?.find(
    option => component.id === option.componentId,
  );

  if (isNil(viewOptions?.overrides)) {
    return component;
  }

  const overrides: Partial<API.AnyComponent> = pickBy(
    viewOptions.overrides,
    (value, key) => handledOverrides.includes(key) && !isNull(value),
  );

  return {
    ...component,
    ...overrides,
  } as API.AnyComponent;
}
